import React from 'react';
import { formatAmount } from '../../utils/formatters';

/**
 * PricingSection - Visar prisuppgifter för offert
 * 
 * Hämtar data från PU-thread (sparas när PU godkänns)
 * Visar:
 * - Inkl. moms (arbete, material, framkörning, sophantering)
 * - 25% moms
 * - ROT-avdrag
 * - Att betala
 * 
 * @param {Object} props
 * @param {Object} props.offert - Offert-objektet
 * @param {Object} props.procurement - Procurement-objektet
 * @param {boolean} props.isExpanded - Om sektionen är expanderad
 * @param {Function} props.onToggle - Callback för att toggla sektion
 */
const PricingSection = ({ offert, procurement, isExpanded, onToggle }) => {
  // Hämta sparad kundpris-data - kolla både nytt format (customerPricing) och gammalt (puData)
  const kundoffertData = offert.customerPricing?.kundoffert || offert.puData?.customerPricing?.kundoffert;

  // Hämta ROT-antal från rot-objektet, eller fallback till procurement
  const rotAntal = kundoffertData?.rot?.antal || kundoffertData?.rotAntal || procurement?.projectInfo?.rotAvdrag || 0;

  // Hämta moms, ROT-avdrag och att betala från rätt plats
  const moms = kundoffertData?.inklMoms?.moms || kundoffertData?.varav25Moms || 0;
  const rotAvdrag = kundoffertData?.rot?.preliminartAvdrag || kundoffertData?.preliminartROTAvdrag || 0;
  const attBetala = kundoffertData?.rot?.attBetala || kundoffertData?.attBetala || 0;

  // Visa ingenting om kundoffertData saknas
  if (!kundoffertData) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <button
        onClick={onToggle}
        className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <h2 className="text-lg font-semibold text-leadme-gray-dark">Fast pris</h2>
        <svg
          className={`w-5 h-5 text-gray-500 transform transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="px-3 sm:px-6 pb-6">
          <div className="space-y-4">
            {/* Inkl. moms sektion */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Inkl. moms</h3>
              <div className="space-y-2">
                {kundoffertData.inklMoms?.arbete > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Arbete:</span>
                    <span className="font-medium">{formatAmount(kundoffertData.inklMoms.arbete)}</span>
                  </div>
                )}
                {kundoffertData.inklMoms?.arbeteEjROT > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Arbete (ej ROT):</span>
                    <span className="font-medium">{formatAmount(kundoffertData.inklMoms.arbeteEjROT)}</span>
                  </div>
                )}
                {kundoffertData.inklMoms?.material > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Material:</span>
                    <span className="font-medium">{formatAmount(kundoffertData.inklMoms.material)}</span>
                  </div>
                )}
                {kundoffertData.inklMoms?.framkorning > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Framkörning:</span>
                    <span className="font-medium">{formatAmount(kundoffertData.inklMoms.framkorning)}</span>
                  </div>
                )}
                {kundoffertData.inklMoms?.sophantering > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Sophantering:</span>
                    <span className="font-medium">{formatAmount(kundoffertData.inklMoms.sophantering)}</span>
                  </div>
                )}
                {kundoffertData.inklMoms?.total && (
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="font-semibold text-gray-800">Total:</span>
                    <span className="font-semibold text-gray-800">{formatAmount(kundoffertData.inklMoms.total)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Moms och ROT-avdrag */}
            <div className="border-t border-gray-200 pt-4">
              {moms > 0 && (
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">(varav 25% moms):</span>
                  <span className="text-sm text-gray-600">{formatAmount(moms)}</span>
                </div>
              )}
              {rotAvdrag > 0 && (
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-green-600">
                    ROT-avdrag{rotAntal > 0 ? ` (${rotAntal} pers)` : ''}:
                  </span>
                  <span className="text-sm text-green-600">-{formatAmount(rotAvdrag)}</span>
                </div>
              )}
              {attBetala > 0 && (
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-lg font-bold text-leadme-pink">Att betala:</span>
                  <span className="text-lg font-bold text-leadme-pink">{formatAmount(attBetala)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingSection;

